import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

interface Crumb {
  label: string;
  path?: string; // if no path → it's the last crumb (non-clickable)
}

interface BreadcrumbProps {
  pageTitle?: string;
  breadcrumbs?: Crumb[]; // optional now
}

const PageBreadcrumb: React.FC<BreadcrumbProps> = ({ pageTitle, breadcrumbs: manualBreadcrumbs = [] }) => {
  const location = useLocation();

  // Save query params to sessionStorage to preserve state (pagination/filters)
  useEffect(() => {
    if (location.search) {
      sessionStorage.setItem(`last_query_${location.pathname}`, location.search);
    }
  }, [location.pathname, location.search]);

  // Helper to get path with preserved query params
  const getPathWithQuery = (path: string) => {
    const savedQuery = sessionStorage.getItem(`last_query_${path}`);
    return savedQuery ? `${path}${savedQuery}` : path;
  };

  // Generate dynamic breadcrumbs if manual ones are not provided
  const generateBreadcrumbs = (): Crumb[] => {
    if (manualBreadcrumbs.length > 0) {
      return manualBreadcrumbs.map((crumb) => ({
        ...crumb,
        path: crumb.path ? getPathWithQuery(crumb.path) : undefined,
      }));
    }

    const pathnames = location.pathname.split("/").filter((x) => x);
    return pathnames.map((segment, index) => {
      const path = `/${pathnames.slice(0, index + 1).join("/")}`;
      const isLast = index === pathnames.length - 1;

      // Format segment name: user-details -> User Details
      const label = segment
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

      return {
        label: isLast && pageTitle ? pageTitle : label,
        path: isLast ? undefined : getPathWithQuery(path),
      };
    });
  };

  const breadcrumbs = generateBreadcrumbs();
  const displayTitle = pageTitle || (breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1].label : "Dashboard");

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
      <h2 className="text-xl font-semibold text-gray-800 dark:text-white/90">
        {displayTitle}
      </h2>
      <nav>
        <ol className="flex items-center gap-1.5">
          <li>
            <Link
              to={getPathWithQuery("/")}
              className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-white transition-colors"
            >
              Home
              <span className="mx-1">/</span>
            </Link>
          </li>
          {breadcrumbs.map((crumb, idx) => (
            <li key={idx} className="flex items-center">
              {crumb.path ? (
                <Link
                  to={crumb.path}
                  className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-white transition-colors"
                >
                  {crumb.label}
                  <span className="mx-1">/</span>
                </Link>
              ) : (
                <span className="text-sm text-gray-800 dark:text-white/90">{crumb.label}</span>
              )}
            </li>
          ))}
        </ol>
      </nav>
    </div>
  );
};

export default PageBreadcrumb;
